import { useState, useRef, useEffect } from "react";

// ── Sample Planning Data ──────────────────────────────────────────────────────
const PLANNING_DATA = {
  season: "Spring 2025",
  department: "Women's Sportswear",
  totalWeeks: 26,
  currentWeek: 10,
  plan: {
    salesRetail: 1200000,
    grossMarginPct: 52.0,
    imuPct: 58.0,
    endingInventoryRetail: 280000,
  },
  actuals: {
    salesToDate: 398000,
    lySalesToDate: 412000,
  },
  classes: [
    {
      id: "cls1",
      name: "Fashion Knit Tops",
      type: "Fashion",
      planSales: 380000,
      actualSales: 108000,
      lySales: 131000,
      onHandRetail: 310000,
      onHandCost: 133300,
      onOrderRetail: 195000,
      onOrderCost: 81900,
      bomInventoryRetail: 290000,
      planEndingInventory: 72000,
      planGrossMarginPct: 54.0,
    },
    {
      id: "cls2",
      name: "Basic Fleece",
      type: "Basic",
      planSales: 290000,
      actualSales: 143000,
      lySales: 138000,
      onHandRetail: 185000,
      onHandCost: 79550,
      onOrderRetail: 98000,
      onOrderCost: 42140,
      bomInventoryRetail: 195000,
      planEndingInventory: 68000,
      planGrossMarginPct: 51.0,
    },
    {
      id: "cls3",
      name: "Fashion Woven Bottoms",
      type: "Fashion",
      planSales: 310000,
      actualSales: 89000,
      lySales: 95000,
      onHandRetail: 248000,
      onHandCost: 111600,
      onOrderRetail: 142000,
      onOrderCost: 63900,
      bomInventoryRetail: 260000,
      planEndingInventory: 62000,
      planGrossMarginPct: 55.0,
    },
    {
      id: "cls4",
      name: "Basic Active Leggings",
      type: "Basic",
      planSales: 220000,
      actualSales: 58000,
      lySales: 48000,
      onHandRetail: 92000,
      onHandCost: 38640,
      onOrderRetail: 74000,
      onOrderCost: 31080,
      bomInventoryRetail: 110000,
      planEndingInventory: 78000,
      planGrossMarginPct: 49.0,
    },
  ],
};

// ── System Prompt ─────────────────────────────────────────────────────────────
function buildSystemPrompt(data) {
  const weeksRemaining = data.totalWeeks - data.currentWeek;
  const salesPaceWeekly = data.actuals.salesToDate / data.currentWeek;
  const projectedSeasonSales = salesPaceWeekly * data.totalWeeks;
  const salesVariance = projectedSeasonSales - data.plan.salesRetail;

  const classDetails = data.classes
    .map((c) => {
      const weeklySalesPace = c.actualSales / data.currentWeek;
      const projectedSales = weeklySalesPace * data.totalWeeks;
      const projectedEOS =
        c.onHandRetail + c.onOrderRetail - (projectedSales - c.actualSales);
      const overstockVsPlan = projectedEOS - c.planEndingInventory;
      const sellThruPct = (c.actualSales / c.planSales) * 100;
      const otbPosition = c.planEndingInventory - c.onHandRetail - c.onOrderRetail + (c.planSales - c.actualSales);
      const maintainedMarginAtRisk =
        overstockVsPlan > 0
          ? overstockVsPlan * (1 - 1 / (1 + c.planGrossMarginPct / 100)) * 0.3
          : 0;

      return `
CLASS: ${c.name} (${c.type})
- Plan Sales: $${c.planSales.toLocaleString()} | Actual to Date: $${c.actualSales.toLocaleString()} | LY: $${c.lySales.toLocaleString()}
- Sell-Through vs Plan: ${sellThruPct.toFixed(1)}%
- On Hand: $${c.onHandRetail.toLocaleString()} retail / $${c.onHandCost.toLocaleString()} cost
- On Order (remaining): $${c.onOrderRetail.toLocaleString()} retail / $${c.onOrderCost.toLocaleString()} cost
- Projected EOS Inventory: $${projectedEOS.toLocaleString()} vs Plan $${c.planEndingInventory.toLocaleString()}
- EOS Overstock Risk: $${overstockVsPlan.toLocaleString()} ${overstockVsPlan > 0 ? "⚠️ OVER" : "✓ OK"}
- OTB Position: $${otbPosition.toLocaleString()} ${otbPosition < 0 ? "⚠️ OVERBOUGHT" : "✓ OPEN"}
- Plan Gross Margin: ${c.planGrossMarginPct}% | Maintained Margin at Risk: $${maintainedMarginAtRisk.toLocaleString()}`;
    })
    .join("\n");

  return `You are an expert Merchandise Financial Planning (MFP) AI agent — a seasoned retail planning analyst with deep expertise in Oracle RPAS-based planning systems, open-to-buy management, markdown optimization, and inventory profitability analysis.

You are analyzing the following live planning data for ${data.department}, ${data.season}. We are in Week ${data.currentWeek} of ${data.totalWeeks}.

DEPARTMENT SUMMARY:
- Season Plan Sales: $${data.plan.salesRetail.toLocaleString()}
- Actuals to Date: $${data.actuals.salesToDate.toLocaleString()} | LY Same Period: $${data.actuals.lySalesToDate.toLocaleString()}
- Projected Season Sales at Current Pace: $${projectedSeasonSales.toLocaleString()} (${salesVariance < 0 ? "⚠️ $" + Math.abs(salesVariance).toLocaleString() + " BELOW plan" : "$" + salesVariance.toLocaleString() + " ABOVE plan"})
- Plan Gross Margin: ${data.plan.grossMarginPct}% | Plan IMU: ${data.plan.imuPct}%
- Plan Ending Inventory: $${data.plan.endingInventoryRetail.toLocaleString()}
- Weeks Remaining: ${weeksRemaining}

CLASS-LEVEL DETAIL:
${classDetails}

YOUR REASONING FRAMEWORK — always think through problems in this sequence:
1. TRIAGE: Flag which classes are at risk (overstock, underperforming sell-through, margin exposure)
2. CLASSIFY: Distinguish Basic vs. Fashion items — the action path is completely different
3. BASIC ITEM PATH: Recommend PO cancellations, push-outs, or store redistribution before markdown
4. FASHION ITEM PATH: Check allocation correctness first (right stores, right size curve), then build markdown budget with least margin damage
5. MARGIN QUANTIFICATION: Always quantify the $ margin at risk and the $ impact of each recommendation
6. HANDOFF: Clearly flag when an action requires the Allocation team (store-level reallocation, size curve optimization)

COMMUNICATION STYLE:
- Be direct and confident — you are a senior analyst, not a chatbot
- Lead with the most urgent issue first
- Use retail planning terminology naturally (OTB, EOS, IMU, maintained margin, sell-through, receipt flow)
- Give specific $ recommendations, not vague guidance
- When you recommend a markdown, specify timing, depth, and expected margin impact
- Keep responses focused and scannable — planners are busy
- Format key metrics in clear sections when helpful

You have access to the full planning data above. Answer any question about the department, diagnose problems, and make specific actionable recommendations.`;
}

// ── Utility ───────────────────────────────────────────────────────────────────
function formatCurrency(n) {
  if (Math.abs(n) >= 1000000) return `$${(n / 1000000).toFixed(1)}M`;
  if (Math.abs(n) >= 1000) return `$${(n / 1000).toFixed(0)}K`;
  return `$${n.toLocaleString()}`;
}

// ── Metric Card ───────────────────────────────────────────────────────────────
function MetricCard({ label, value, sub, status }) {
  const statusColors = {
    good: "#00d4aa",
    warn: "#f5a623",
    bad: "#ff4d6d",
    neutral: "#8b9dc3",
  };
  const color = statusColors[status] || statusColors.neutral;
  return (
    <div style={{
      background: "rgba(255,255,255,0.04)",
      border: `1px solid rgba(255,255,255,0.08)`,
      borderLeft: `3px solid ${color}`,
      borderRadius: "8px",
      padding: "14px 16px",
      minWidth: 0,
    }}>
      <div style={{ fontSize: "11px", color: "#8b9dc3", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "6px", fontFamily: "inherit" }}>{label}</div>
      <div style={{ fontSize: "22px", fontWeight: "700", color: "#eef2ff", fontFamily: "'DM Mono', monospace", letterSpacing: "-0.02em" }}>{value}</div>
      {sub && <div style={{ fontSize: "11px", color: color, marginTop: "4px" }}>{sub}</div>}
    </div>
  );
}

// ── Class Badge ───────────────────────────────────────────────────────────────
function ClassBadge({ cls }) {
  const isFashion = cls.type === "Fashion";
  const weeklySalesPace = cls.actualSales / PLANNING_DATA.currentWeek;
  const projectedSales = weeklySalesPace * PLANNING_DATA.totalWeeks;
  const projectedEOS = cls.onHandRetail + cls.onOrderRetail - (projectedSales - cls.actualSales);
  const overstockVsPlan = projectedEOS - cls.planEndingInventory;
  const sellThruPct = ((cls.actualSales / cls.planSales) * 100).toFixed(0);
  const isRisk = overstockVsPlan > 20000;

  return (
    <div style={{
      background: "rgba(255,255,255,0.03)",
      border: `1px solid ${isRisk ? "rgba(255,77,109,0.3)" : "rgba(255,255,255,0.07)"}`,
      borderRadius: "8px",
      padding: "12px 14px",
      display: "flex",
      flexDirection: "column",
      gap: "6px",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: "12px", fontWeight: "600", color: "#eef2ff" }}>{cls.name}</span>
        <span style={{
          fontSize: "10px", fontWeight: "700", padding: "2px 8px", borderRadius: "20px",
          background: isFashion ? "rgba(245,166,35,0.15)" : "rgba(0,212,170,0.15)",
          color: isFashion ? "#f5a623" : "#00d4aa",
          letterSpacing: "0.05em",
        }}>{cls.type.toUpperCase()}</span>
      </div>
      <div style={{ display: "flex", gap: "16px" }}>
        <div>
          <div style={{ fontSize: "10px", color: "#8b9dc3" }}>ST%</div>
          <div style={{ fontSize: "14px", fontWeight: "700", color: sellThruPct < 70 ? "#ff4d6d" : "#00d4aa", fontFamily: "'DM Mono', monospace" }}>{sellThruPct}%</div>
        </div>
        <div>
          <div style={{ fontSize: "10px", color: "#8b9dc3" }}>EOS Risk</div>
          <div style={{ fontSize: "14px", fontWeight: "700", color: isRisk ? "#ff4d6d" : "#00d4aa", fontFamily: "'DM Mono', monospace" }}>
            {isRisk ? `+${formatCurrency(overstockVsPlan)}` : "✓ OK"}
          </div>
        </div>
        <div>
          <div style={{ fontSize: "10px", color: "#8b9dc3" }}>GM%</div>
          <div style={{ fontSize: "14px", fontWeight: "700", color: "#eef2ff", fontFamily: "'DM Mono', monospace" }}>{cls.planGrossMarginPct}%</div>
        </div>
      </div>
    </div>
  );
}

// ── Message Bubble ────────────────────────────────────────────────────────────
function MessageBubble({ msg }) {
  const isUser = msg.role === "user";
  return (
    <div style={{
      display: "flex",
      justifyContent: isUser ? "flex-end" : "flex-start",
      marginBottom: "16px",
      gap: "10px",
      alignItems: "flex-start",
    }}>
      {!isUser && (
        <div style={{
          width: "32px", height: "32px", borderRadius: "50%", flexShrink: 0,
          background: "linear-gradient(135deg, #4f6ef7, #00d4aa)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: "14px", fontWeight: "700", color: "#fff",
        }}>M</div>
      )}
      <div style={{
        maxWidth: "82%",
        background: isUser
          ? "linear-gradient(135deg, #4f6ef7, #3a56d4)"
          : "rgba(255,255,255,0.05)",
        border: isUser ? "none" : "1px solid rgba(255,255,255,0.08)",
        borderRadius: isUser ? "18px 18px 4px 18px" : "4px 18px 18px 18px",
        padding: "12px 16px",
        color: "#eef2ff",
        fontSize: "14px",
        lineHeight: "1.65",
        whiteSpace: "pre-wrap",
      }}>
        {msg.content}
      </div>
      {isUser && (
        <div style={{
          width: "32px", height: "32px", borderRadius: "50%", flexShrink: 0,
          background: "rgba(255,255,255,0.1)",
          border: "1px solid rgba(255,255,255,0.15)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: "14px", color: "#8b9dc3",
        }}>L</div>
      )}
    </div>
  );
}

// ── Suggested Prompts ─────────────────────────────────────────────────────────
const SUGGESTED = [
  "What's my biggest risk this season?",
  "Which classes should I be worried about?",
  "What should I do about my Fashion Knit Tops?",
  "Build me a markdown plan for overstocked classes",
  "Can I cancel any POs on my Basic classes?",
  "What's my projected end-of-season margin?",
];

// ── Main App ──────────────────────────────────────────────────────────────────
export default function MFPAgent() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [started, setStarted] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  const d = PLANNING_DATA;
  const salesPace = d.actuals.salesToDate / d.currentWeek;
  const projectedSales = salesPace * d.totalWeeks;
  const salesVariancePct = (((projectedSales - d.plan.salesRetail) / d.plan.salesRetail) * 100).toFixed(1);
  const lyVariancePct = (((d.actuals.salesToDate - d.actuals.lySalesToDate) / d.actuals.lySalesToDate) * 100).toFixed(1);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  async function sendMessage(text) {
    const userMsg = text || input.trim();
    if (!userMsg) return;
    setInput("");
    setStarted(true);

    const newMessages = [...messages, { role: "user", content: userMsg }];
    setMessages(newMessages);
    setLoading(true);

    try {
      // Calls our Netlify serverless function (netlify/functions/chat.js)
      // instead of api.anthropic.com directly — keeps the API key server-side
      // and avoids CORS issues.
      const response = await fetch("/.netlify/functions/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          system: buildSystemPrompt(d),
          messages: newMessages.map((m) => ({ role: m.role, content: m.content })),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Request failed");
      }

      const reply = data.content?.find((b) => b.type === "text")?.text || "I couldn't generate a response.";
      setMessages([...newMessages, { role: "assistant", content: reply }]);
    } catch (err) {
      setMessages([...newMessages, { role: "assistant", content: "Connection error. Please try again." }]);
    } finally {
      setLoading(false);
    }
  }

  function handleKey(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }

  return (
    <div style={{
      fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
      background: "#0d1117",
      minHeight: "100vh",
      color: "#eef2ff",
      display: "flex",
      flexDirection: "column",
    }}>
      {/* Google Fonts */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Mono:wght@400;500;700&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 2px; }
        textarea:focus { outline: none; }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
        @keyframes fadeIn { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
      `}</style>

      {/* Header */}
      <div style={{
        borderBottom: "1px solid rgba(255,255,255,0.07)",
        padding: "16px 24px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        background: "rgba(255,255,255,0.02)",
        backdropFilter: "blur(10px)",
        position: "sticky", top: 0, zIndex: 10,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div style={{
            width: "36px", height: "36px", borderRadius: "10px",
            background: "linear-gradient(135deg, #4f6ef7, #00d4aa)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: "16px", fontWeight: "800",
          }}>M</div>
          <div>
            <div style={{ fontWeight: "700", fontSize: "15px", letterSpacing: "-0.01em" }}>MFP Agent</div>
            <div style={{ fontSize: "11px", color: "#8b9dc3" }}>Merchandise Financial Planning · AI Co-Pilot</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <div style={{
            fontSize: "11px", color: "#00d4aa", background: "rgba(0,212,170,0.1)",
            border: "1px solid rgba(0,212,170,0.2)", borderRadius: "20px",
            padding: "4px 10px", fontWeight: "600",
          }}>● LIVE</div>
          <div style={{ fontSize: "12px", color: "#8b9dc3" }}>{d.department} · {d.season} · Wk {d.currentWeek}/{d.totalWeeks}</div>
        </div>
      </div>

      <div style={{ flex: 1, display: "flex", flexDirection: "column", maxWidth: "1100px", width: "100%", margin: "0 auto", padding: "0 20px" }}>

        {/* Metrics Strip */}
        <div style={{
          display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "12px",
          padding: "20px 0 16px",
        }}>
          <MetricCard
            label="Sales to Date"
            value={formatCurrency(d.actuals.salesToDate)}
            sub={`${lyVariancePct > 0 ? "+" : ""}${lyVariancePct}% vs LY`}
            status={lyVariancePct >= 0 ? "good" : "bad"}
          />
          <MetricCard
            label="Projected Season"
            value={formatCurrency(projectedSales)}
            sub={`${salesVariancePct > 0 ? "+" : ""}${salesVariancePct}% vs plan`}
            status={salesVariancePct >= -3 ? "warn" : "bad"}
          />
          <MetricCard
            label="Week"
            value={`${d.currentWeek} of ${d.totalWeeks}`}
            sub={`${d.totalWeeks - d.currentWeek} weeks remaining`}
            status="neutral"
          />
          <MetricCard
            label="Plan GM%"
            value={`${d.plan.grossMarginPct}%`}
            sub={`Plan IMU ${d.plan.imuPct}%`}
            status="neutral"
          />
        </div>

        {/* Class Grid */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "10px", marginBottom: "20px" }}>
          {d.classes.map((c) => <ClassBadge key={c.id} cls={c} />)}
        </div>

        {/* Chat Area */}
        <div style={{
          flex: 1,
          background: "rgba(255,255,255,0.02)",
          border: "1px solid rgba(255,255,255,0.07)",
          borderRadius: "16px",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          minHeight: "360px",
          marginBottom: "16px",
        }}>
          <div style={{ flex: 1, overflowY: "auto", padding: "20px" }}>
            {!started && (
              <div style={{ animation: "fadeIn 0.5s ease", textAlign: "center", padding: "32px 0 16px" }}>
                <div style={{
                  width: "56px", height: "56px", borderRadius: "50%", margin: "0 auto 16px",
                  background: "linear-gradient(135deg, #4f6ef7, #00d4aa)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: "24px", fontWeight: "800",
                }}>M</div>
                <div style={{ fontSize: "18px", fontWeight: "700", marginBottom: "8px" }}>Ready to analyze your plan</div>
                <div style={{ fontSize: "13px", color: "#8b9dc3", maxWidth: "420px", margin: "0 auto 24px" }}>
                  I have your full {d.department} {d.season} data loaded — class-level sales, inventory, on-order, and margin. Ask me anything.
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", justifyContent: "center" }}>
                  {SUGGESTED.map((s) => (
                    <button key={s} onClick={() => sendMessage(s)} style={{
                      background: "rgba(79,110,247,0.12)", border: "1px solid rgba(79,110,247,0.25)",
                      color: "#a0b0ff", borderRadius: "20px", padding: "7px 14px",
                      fontSize: "12px", cursor: "pointer", fontFamily: "inherit",
                      transition: "all 0.2s",
                    }}
                      onMouseEnter={e => e.target.style.background = "rgba(79,110,247,0.25)"}
                      onMouseLeave={e => e.target.style.background = "rgba(79,110,247,0.12)"}
                    >{s}</button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <div key={i} style={{ animation: "fadeIn 0.3s ease" }}>
                <MessageBubble msg={m} />
              </div>
            ))}

            {loading && (
              <div style={{ display: "flex", gap: "10px", alignItems: "flex-start", marginBottom: "16px" }}>
                <div style={{
                  width: "32px", height: "32px", borderRadius: "50%", flexShrink: 0,
                  background: "linear-gradient(135deg, #4f6ef7, #00d4aa)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: "14px", fontWeight: "700",
                }}>M</div>
                <div style={{
                  background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)",
                  borderRadius: "4px 18px 18px 18px", padding: "14px 18px",
                  display: "flex", gap: "6px", alignItems: "center",
                }}>
                  {[0, 1, 2].map(i => (
                    <div key={i} style={{
                      width: "7px", height: "7px", borderRadius: "50%",
                      background: "#4f6ef7", animation: `pulse 1.2s ease ${i * 0.2}s infinite`,
                    }} />
                  ))}
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div style={{
            borderTop: "1px solid rgba(255,255,255,0.07)",
            padding: "14px 16px",
            display: "flex", gap: "10px", alignItems: "flex-end",
          }}>
            <textarea
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder="Ask about your plan, inventory risk, markdowns, OTB..."
              rows={1}
              style={{
                flex: 1, background: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(255,255,255,0.1)", borderRadius: "10px",
                padding: "10px 14px", color: "#eef2ff", fontSize: "14px",
                fontFamily: "inherit", resize: "none", lineHeight: "1.5",
                maxHeight: "100px", overflow: "auto",
              }}
            />
            <button
              onClick={() => sendMessage()}
              disabled={loading || !input.trim()}
              style={{
                width: "40px", height: "40px", borderRadius: "10px", flexShrink: 0,
                background: loading || !input.trim() ? "rgba(79,110,247,0.3)" : "linear-gradient(135deg, #4f6ef7, #3a56d4)",
                border: "none", cursor: loading || !input.trim() ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
                transition: "all 0.2s", color: "#fff", fontSize: "18px",
              }}
            >↑</button>
          </div>
        </div>

        {/* Footer */}
        <div style={{ fontSize: "11px", color: "rgba(139,157,195,0.5)", textAlign: "center", paddingBottom: "16px" }}>
          MFP Agent · {d.department} · {d.season} · Data as of Week {d.currentWeek}
        </div>
      </div>
    </div>
  );
}
